/**
 * Выполнимость варианта ПП — этап 4 планирования сезона (UC-4).
 *
 * Макет (утверждён founder):
 * `docs/superpowers/prototypes/season-planning-2027/05-feasibility.html`.
 *
 * Язык — агронома: «агроокно», «сцепка», «нужно 6, есть 4», «риск принят».
 * Модельные слова (scenario, bottleneck_key, severity) наружу не показываем —
 * они живут только в типах и адресах запросов.
 *
 * Ключи запросов держим ЗДЕСЬ, а не в общем `queryKeys.ts`: после принятия
 * риска протухает только этот экран (расчёт нигде не кэшируется на бэке и не
 * меняет ни строк плана, ни техкарт), поэтому общий неймспейс ему не нужен.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { techCardErrorText } from '@/lib/planning/techCards';
import { toast } from '@/lib/toast';

export const feasibilityKeys = {
  all: () => ['planning', 'feasibility'] as const,
  check: (scenarioId: string | null, mode: FeasibilityMode) =>
    ['planning', 'feasibility', scenarioId ?? 'none', mode] as const,
};

/** `auto` — полный, если парк хозяйства заведён, иначе базовый (Р9). */
export type FeasibilityMode = 'auto' | 'basic' | 'full';

/**
 * Что со строкой окна:
 *   `deficit`  — сцепок нужно больше, чем есть (полный режим);
 *   `overlap`  — своё окно закрывается, но в те же дни та же сцепка занята другим;
 *   `tight`    — нужно ровно столько, сколько есть, запаса нет;
 *   `peak`     — базовый режим: именно это окно диктует, сколько сцепок держать;
 *   `demand`   — базовый режим: потребность посчитана, парк не диктует;
 *   `unknown_machinery` — в строке карты не указана техника, сверять не с чем;
 *   `ok`       — хватает.
 */
export type WindowSeverity =
  | 'deficit'
  | 'overlap'
  | 'tight'
  | 'peak'
  | 'demand'
  | 'unknown_machinery'
  | 'ok';

export interface FeasibilityWindow {
  key: string;
  label: string;
  machinery_label: string;
  starts_on: string;
  ends_on: string;
  work_days: number;
  volume: number;
  volume_unit: string;
  units_needed: number;
  concurrent_units: number;
  available: number | null;
  deficit: number | null;
  severity: WindowSeverity;
  accepted: boolean;
}

export interface FeasibilityGate {
  ok: boolean;
  rows_total: number;
  rows_covered: number;
  area_total_ha: number;
  area_uncovered_ha: number;
  uncovered: {
    crop_name: string | null;
    predecessor_name: string | null;
    fields_count: number;
    area_ha: number;
  }[];
}

export interface AcceptedRisk {
  bottleneck_key: string;
  comment: string;
  accepted_by: string | null;
  accepted_at: string;
  snapshot: Record<string, unknown>;
}

export interface FeasibilityExclusion {
  operation_id: string;
  label: string;
  card_name: string;
  reason: string;
  reason_text: string;
}

export type FeasibilityStatus =
  | 'feasible'
  | 'tight'
  | 'deficit'
  | 'demand_only'
  | 'gate_blocked';

export interface Feasibility {
  scenario: { id: string; name: string; farm_name: string; season_year: number };
  mode: 'basic' | 'full';
  full_mode_available: boolean;
  can_skip: boolean;
  checked_at: string | null;
  checked_status: string | null;
  gate: FeasibilityGate;
  fleet: { machines: number; machines_without_farm: number; units_available: Record<string, number> };
  verdict: {
    status: FeasibilityStatus;
    windows: number;
    bottlenecks: number;
    bottlenecks_open: number;
    accepted: number;
    peak_units: number;
  };
  windows: FeasibilityWindow[];
  bottlenecks: FeasibilityWindow[];
  accepted_risks: AcceptedRisk[];
  exclusions: FeasibilityExclusion[];
}

export function useFeasibility(scenarioId: string | null, mode: FeasibilityMode = 'auto') {
  return useQuery<Feasibility, Error>({
    queryKey: feasibilityKeys.check(scenarioId, mode),
    queryFn: () =>
      api.get<Feasibility>(`/api/v1/planning/scenarios/${scenarioId}/feasibility?mode=${mode}`),
    enabled: !!scenarioId,
    staleTime: 15_000,
  });
}

function useInvalidateFeasibility() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: feasibilityKeys.all() });
}

/** Принять риск по узкому месту (Р17): комментарий обязателен. */
export function useAcceptRisk() {
  const invalidate = useInvalidateFeasibility();
  return useMutation<unknown, Error, { scenarioId: string; key: string; comment: string }>({
    mutationFn: ({ scenarioId, key, comment }) =>
      api.post(`/api/v1/planning/scenarios/${scenarioId}/feasibility/risks`, {
        bottleneck_key: key,
        comment,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Риск принят — решение сохранено с вашим именем и комментарием');
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

export function useCancelRisk() {
  const invalidate = useInvalidateFeasibility();
  return useMutation<unknown, Error, { scenarioId: string; key: string }>({
    mutationFn: ({ scenarioId, key }) =>
      api.delete(`/api/v1/planning/scenarios/${scenarioId}/feasibility/risks/${key}`),
    onSuccess: () => {
      invalidate();
      toast.success('Принятие отменено');
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/** Отметить вариант проверенным (Р33). Вердикт считает СЕРВЕР по своему
 *  расчёту — тело запроса пустое, клиентский статус на бэке не принимается. */
export function useMarkChecked() {
  const invalidate = useInvalidateFeasibility();
  return useMutation<unknown, Error, { scenarioId: string }>({
    mutationFn: ({ scenarioId }) =>
      api.post(`/api/v1/planning/scenarios/${scenarioId}/feasibility/checked`, {}),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

// ── Производные для экрана ───────────────────────────────────────────────────

/** «25.04 – 10.05» — как в макете, без года: год у сезона один. */
export function formatWindow(startsOn: string, endsOn: string): string {
  return `${formatDay(startsOn)} – ${formatDay(endsOn)}`;
}

function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

/**
 * Позиция и ширина полосы окна на таймлайне сезона, в процентах.
 *
 * Границы шкалы берутся из САМИХ окон, а не из календарного года: показывать
 * пустые январь и декабрь, когда все работы с марта по октябрь, — значит
 * сжать полезную часть картинки вдвое.
 */
export function timelineBar(
  window: { starts_on: string; ends_on: string },
  scale: { from: number; to: number },
): { left: number; width: number } {
  const span = Math.max(scale.to - scale.from, 1);
  const start = new Date(`${window.starts_on}T00:00:00`).getTime();
  const end = new Date(`${window.ends_on}T00:00:00`).getTime();
  const left = ((start - scale.from) / span) * 100;
  const width = ((end - start) / span) * 100;
  return { left: clamp(left), width: Math.max(clamp(width), 1.5) };
}

function clamp(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(Math.max(value, 0), 100);
}

export function timelineScale(windows: { starts_on: string; ends_on: string }[]): {
  from: number;
  to: number;
  months: string[];
} {
  const starts = windows.map((w) => new Date(`${w.starts_on}T00:00:00`).getTime());
  const ends = windows.map((w) => new Date(`${w.ends_on}T00:00:00`).getTime());
  const from = starts.length ? Math.min(...starts) : Date.now();
  const to = ends.length ? Math.max(...ends) : from + 1;
  const months: string[] = [];
  const cursor = new Date(from);
  cursor.setDate(1);
  while (cursor.getTime() <= to) {
    months.push(cursor.toLocaleDateString('ru-RU', { month: 'short' }));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return { from, to, months };
}

/** Итог одной фразой — то, что человек читает первым. */
export function verdictTitle(data: Feasibility): string {
  switch (data.verdict.status) {
    case 'gate_blocked':
      return 'Считать нечего: не у каждой строки плана есть техкарта';
    case 'demand_only':
      return `Потребность посчитана: в пике нужно ${data.verdict.peak_units} сцепок`;
    case 'deficit':
      return `Техники не хватает: ${data.verdict.bottlenecks_open} узких мест`;
    case 'tight':
      return 'Успеваем впритык — запаса нет';
    default:
      return 'Парка хватает на все агроокна';
  }
}

export function volumeText(window: FeasibilityWindow): string {
  return `${Math.round(window.volume).toLocaleString('ru-RU')} ${window.volume_unit}`;
}

/** «нужно 6 · есть 4» либо «нужно 6» — в базовом режиме сравнивать не с чем. */
export function needText(window: FeasibilityWindow): string {
  if (window.available === null) return `нужно ${window.units_needed}`;
  return `нужно ${window.units_needed} · есть ${window.available}`;
}
